import React, { useState, useRef, useEffect } from 'react'
import styles from './tour.scss'
import PropTypes from 'prop-types'
import ArrowIcon from 'assets/icons/ico-arrow.svg'
import SvgIcon from 'src/components/common/SvgIcon'
import classnames from 'classnames'
import Button from 'src/components/common/Button'

const HIGHLIGHT_PADDING = 10
const HIGHLIGHT_RADIUS = 10

const Tour = ({
  steps = [],
  current = 0,
  open = false,
  onFinish,
  onClose,
  onSkipTutorials,
  buttonClassName,
  tourCardContentClassName,
  tourContentClassName,
  mask = true,
  arrow = true,
  highlightPadding = HIGHLIGHT_PADDING,
  highlightRadius = HIGHLIGHT_RADIUS,
  showSkipButton = true,
  tourTitleClassName,
  tourDescriptionClassName
}) => {
  const [currentStep, setCurrentStep] = useState(current)
  const tourCardRef = useRef(null)

  const [svgSize, setSvgSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  })

  const [highlightRect, setHighlightRect] = useState(null)

  const getHighlightRect = () => {
    const currentStepConfig = steps[currentStep]
    if (!currentStepConfig) return null
    let targetEl = typeof currentStepConfig.target === 'function'
      ? currentStepConfig.target()
      : currentStepConfig.target?.current
    if (!targetEl) return null
    const rect = targetEl.getBoundingClientRect()
    const pad = parsePadding(highlightPadding)
    return {
      x: rect.left - pad.left,
      y: rect.top - pad.top,
      width: rect.width + pad.left + pad.right,
      height: rect.height + pad.top + pad.bottom,
      radius: highlightRadius
    }
  }

  const calculatePosition = () => {
    const currentStepConfig = steps[currentStep]
    if (!currentStepConfig || !tourCardRef.current) return { top: 0, left: 0, display: 'none' }

    let targetEl = null
    if (typeof currentStepConfig.target === 'function') {
      targetEl = currentStepConfig.target()
    } else {
      targetEl = currentStepConfig.target.current
    }

    const placement = currentStepConfig.placement || 'bottom'
    const cardEl = tourCardRef.current
    const cardRect = cardEl.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const scrollTop = window.scrollY
    const scrollLeft = window.scrollX

    if (!targetEl) {
      return {
        top: `${scrollTop + viewportHeight / 2 - cardRect.height / 2}px`,
        left: `${scrollLeft + viewportWidth / 2 - cardRect.width / 2}px`,
        display: 'block'
      }
    }

    const targetRect = targetEl.getBoundingClientRect()
    const pad = parsePadding(highlightPadding)
    const style = { display: 'block' }

    switch (placement) {
      case 'top':
        style.top = `${targetRect.top - pad.top - cardRect.height - 20}px`
        style.left = `${targetRect.left + targetRect.width / 2 - cardRect.width / 2}px`
        break
      case 'bottom':
        style.top = `${targetRect.bottom + pad.bottom + 20}px`
        style.left = `${targetRect.left + targetRect.width / 2 - cardRect.width / 2}px`
        break
      case 'left':
        style.top = `${targetRect.top + targetRect.height / 2 - cardRect.height / 2}px`
        style.left = `${targetRect.left - pad.left - cardRect.width - 20}px`
        break
      case 'right':
        const highlightTop = targetRect.top - pad.top
        const highlightHeight = targetRect.height + pad.top + pad.bottom
        style.top = `${highlightTop + highlightHeight / 2 - cardRect.height / 2}px`
        style.left = `${targetRect.right + pad.right + 20}px`
        break
      case 'center':
        style.top = `${window.innerHeight / 2 - cardRect.height / 2}px`
        style.left = `${window.innerWidth / 2 - cardRect.width / 2}px`
        break
      case 'bottom-right': {
        style.top = `${targetRect.bottom + 20}px`
        style.left = `${targetRect.right - cardRect.width}px`
        break
      }
      default:
        style.top = `${targetRect.bottom + pad.bottom + 20}px`
        style.left = `${targetRect.left + targetRect.width / 2 - cardRect.width / 2}px`
    }

    return style
  }

  function parsePadding(padding) {
    if (typeof padding === 'number') {
      return { top: padding, right: padding, bottom: padding, left: padding }
    }
    if (Array.isArray(padding)) {
      if (padding.length === 2) {
        return { top: padding[0], right: padding[1], bottom: padding[0], left: padding[1] }
      }
      if (padding.length === 4) {
        return { top: padding[0], right: padding[1], bottom: padding[2], left: padding[3] }
      }
    }
    if (typeof padding === 'string') {
      const arr = padding.split(' ').map(Number)
      if (arr.length === 2) {
        return { top: arr[0], right: arr[1], bottom: arr[0], left: arr[1] }
      }
      if (arr.length === 4) {
        return { top: arr[0], right: arr[1], bottom: arr[2], left: arr[3] }
      }
    }
    return { top: HIGHLIGHT_PADDING, right: HIGHLIGHT_PADDING, bottom: HIGHLIGHT_PADDING, left: HIGHLIGHT_PADDING }
  }

  useEffect(() => {
    function updateSvgSize() {
      setSvgSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }
    window.addEventListener('resize', updateSvgSize)
    window.addEventListener('scroll', updateSvgSize)
    return () => {
      window.removeEventListener('resize', updateSvgSize)
      window.removeEventListener('scroll', updateSvgSize)
    }
  }, [])

  useEffect(() => {
    if (!open) return

    setHighlightRect(getHighlightRect())
    const cardStyle = calculatePosition()
    if (tourCardRef.current) {
      Object.assign(tourCardRef.current.style, cardStyle)
    }

    const handleResizeOrScroll = () => {
      setHighlightRect(getHighlightRect())
      const cardStyle = calculatePosition()
      if (tourCardRef.current) {
        Object.assign(tourCardRef.current.style, cardStyle)
      }
    }

    window.addEventListener('resize', handleResizeOrScroll)
    window.addEventListener('scroll', handleResizeOrScroll)
    return () => {
      window.removeEventListener('resize', handleResizeOrScroll)
      window.removeEventListener('scroll', handleResizeOrScroll)
    }
  }, [open, currentStep, steps, highlightPadding])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1)
    } else {
      onFinish?.()
      onClose?.()
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }

  const svgWidth = svgSize.width
  const svgHeight = svgSize.height

  if (!open) return null

  const currentStepConfig = steps[currentStep] || {}
  const showArrow = arrow

  return (
    <>
      {open && mask && (
        <svg
          width={svgWidth}
          height={svgHeight}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 1000,
            pointerEvents: 'auto'
          }}
        >
          <defs>
            <mask id="tour-mask">
              <rect x="0" y="0" width={svgWidth} height={svgHeight} fill="white" />
              {highlightRect && (
                <rect
                  x={highlightRect.x}
                  y={highlightRect.y}
                  width={highlightRect.width}
                  height={highlightRect.height}
                  rx={highlightRect.radius}
                  ry={highlightRect.radius}
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width={svgWidth}
            height={svgHeight}
            fill="rgba(0,0,0,0.6)"
            mask="url(#tour-mask)"
            onClick={onClose}
          />
        </svg>
      )}

      <div ref={tourCardRef} className={classnames(styles['tour-card'])}>
        <div className={classnames(styles['tour-card-content'], styles[`tour-card-content-${currentStepConfig.placement || 'bottom'}`], tourCardContentClassName)}>
          {
            showArrow && (
              <SvgIcon divClassName={styles['tour-arrow-wrapper']} className={classnames(styles['tour-arrow'], styles[currentStepConfig.placement || 'bottom'])} icon={ArrowIcon} width={24} height={24} />
            )
          }

          <div className={classnames(styles['tour-content'], styles[`tour-content-${currentStepConfig.placement || 'bottom'}`], tourContentClassName)}>
            {currentStepConfig.title && (
              <div className={classnames(styles['tour-title'], tourTitleClassName)}>{currentStepConfig.title}</div>
            )}

            {currentStepConfig.description && (
              <div className={classnames(styles['tour-description'], tourDescriptionClassName)}>{currentStepConfig.description}</div>
            )}

            <div className={styles['tour-actions']}>
              {
                steps.length > 1 && (
                  <>
                    <Button
                      className={`${styles['tour-btn']} ${styles['tour-btn-prev']} ${buttonClassName}`}
                      onClick={handlePrev}
                      disabled={currentStep === 0}
                    >
                      Previous
                    </Button>

                    <Button
                      className={`${styles['tour-btn']} ${buttonClassName}`}
                      onClick={handleNext}
                    >
                      {currentStep === steps.length - 1 ? 'OK' : 'Next'}
                    </Button>

                  </>
                )
              }

              {
                steps.length === 1 && (
                  <>
                    {
                      showSkipButton && (
                        <Button className={`${styles['tour-btn']} ${styles['tour-btn-prev']} ${buttonClassName}`} onClick={onSkipTutorials}>
                          Skip Tutorials
                        </Button>
                      )
                    }

                    <Button className={`${styles['tour-btn']} ${buttonClassName}`} onClick={onFinish}>
                      OK
                    </Button>
                  </>
                )
              }
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Tour.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
      placement: PropTypes.string,
      title: PropTypes.node,
      description: PropTypes.node
    })
  ),
  current: PropTypes.number,
  open: PropTypes.bool,
  onFinish: PropTypes.func,
  onClose: PropTypes.func,
  onSkipTutorials: PropTypes.func,
  mask: PropTypes.bool,
  arrow: PropTypes.bool,
  highlightPadding: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.array]),
  highlightRadius: PropTypes.number,
  buttonClassName: PropTypes.string,
  tourCardContentClassName: PropTypes.string,
  tourContentClassName: PropTypes.string,
  showSkipButton: PropTypes.bool,
  tourTitleClassName: PropTypes.string,
  tourDescriptionClassName: PropTypes.string
}

export default Tour
